import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { CheckCircle2, XCircle, MinusCircle, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import { fmtBytes, fmtDate } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const DocIcon = ({ d }) => {
  if (!d.selected) return <MinusCircle size={16} className="text-slate-300 shrink-0" />;
  if (d.downloaded) return <CheckCircle2 size={16} className="text-green-600 shrink-0" />;
  return <XCircle size={16} className="text-red-500 shrink-0" />;
};

export default function CrawlSheet({ crawl, onClose }) {
  const copyNames = () => {
    navigator.clipboard.writeText(crawl.documents.filter((d) => d.downloaded).map((d) => d.filename).join("\n"));
    toast.success("Filenames copied");
  };

  return (
    <Sheet open={!!crawl} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="crawl-detail-sheet">
        {crawl && (
          <>
            <SheetHeader className="text-left space-y-2 pr-8">
              <div className="w-fit"><StatusBadge status={crawl.status} /></div>
              <SheetTitle className="font-heading text-xl leading-snug" data-testid="crawl-detail-title">{crawl.case_caption}</SheetTitle>
              <SheetDescription className="space-y-1">
                {crawl.index_number && <span className="block font-mono text-amber-700">Index # {crawl.index_number}</span>}
                <span className="block">{fmtDate(crawl.created_at)} · {crawl.downloaded_count}/{crawl.selected_count} zipped · {fmtBytes(crawl.total_bytes)}</span>
                {crawl.exported_by && <span className="block" data-testid="crawl-detail-author">Exported by {crawl.exported_by}</span>}
              </SheetDescription>
            </SheetHeader>
            <div className="flex gap-2 mt-5">
              <a href={crawl.url} target="_blank" rel="noreferrer" data-testid="crawl-detail-open-nyscef" className="flex-1 h-10 rounded-md bg-[#1E293B] text-white text-sm font-medium hover:bg-blue-600 transition-colors inline-flex items-center justify-center gap-2">
                <ExternalLink size={14} /> Open on NYSCEF to re-zip
              </a>
              <button onClick={copyNames} data-testid="crawl-detail-copy-names" className="h-10 px-4 rounded-md border border-slate-200 text-sm hover:bg-slate-100 transition-colors inline-flex items-center gap-2">
                <Copy size={14} /> Copy names
              </button>
            </div>
            <ul className="mt-6 border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="crawl-detail-doc-list">
              {crawl.documents.map((d, i) => (
                <li key={`${d.url}-${i}`} className="flex gap-3 px-4 py-3" data-testid={`crawl-doc-${i}`}>
                  <DocIcon d={d} />
                  <div className="min-w-0">
                    <div className="font-mono text-xs break-all text-slate-800">{d.filename}</div>
                    {d.description && <div className="text-xs text-slate-600 mt-0.5">{d.description}</div>}
                    <div className="text-xs text-slate-500 mt-0.5">
                      {d.filed_date && `Filed ${d.filed_date} · `}
                      {d.downloaded ? fmtBytes(d.size) : d.selected ? d.error || "Failed" : "Not selected"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
